import dayjs, { Dayjs } from 'dayjs';
import * as z from 'zod';

type ItemStringValidationProps = {
  label: string;
  max: number;
  min: number;
  required: boolean;
  transformer: (item: string | null) => string | null | undefined;
};

export const phoneValidationPattern = /^\+\d{1,3}\d{6,14}$/;

export const nullTransformer = <T extends string>(item: T | null): T | null => (item?.trim() === '' ? null : item);
export const undefinedTransformer = <T extends string>(item: T | null | undefined): T | undefined =>
  item?.trim() === '' || item === null ? undefined : item;

export const itemStringValidator = (options: Partial<ItemStringValidationProps>) => {
  const { label, max = 50, min, required = true, transformer = nullTransformer } = options;

  const minLength = min !== undefined ? min : required ? 1 : 0;

  return z
    .union([z.string(), z.null(), z.undefined()])
    .transform((value) => value ?? '')
    .pipe(
      z
        .string({ error: `${label} is required.` })
        .trim()
        .max(max, { error: `Must be ${max} characters or fewer.` })
        .refine((val) => val.length >= minLength, {
          error: min ? `${label ?? 'Value'} must be at least ${min} characters.` : `${label} is required.`,
        })
    )
    .transform(transformer);
};

export const itemNameValidator = (max = 100) => itemStringValidator({ label: 'Name', max });

export const itemShortNameValidator = (max = 50) =>
  z
    .string()
    .max(max, { error: `Must be ${max} characters or fewer.` })
    .trim()
    .nullable()
    .transform(nullTransformer);

export const itemPositionValidator = z.number().nullable();

export const itemInformationValidator = z
  .string()
  .max(500, { error: 'Must be 500 characters or fewer.' })
  .nullable()
  .transform(nullTransformer);

export const itemIdValidator = (label: string) => z.string({ error: `${label} is required.` });

export const itemEmailValidator = z
  .email({ error: 'Invalid email.' })
  .max(100, { error: 'Must be 100 characters or fewer.' });

export const condititionValueValidator = z.union([z.string(), z.number(), z.null()]).transform((v) => {
  if (v === '' || v === null) return null;
  return typeof v === 'number' ? v : parseFloat(v);
});

export const isEighteenOrOlder = (date: Dayjs) => {
  return date.isBefore(dayjs().subtract(18, 'years'));
};

export const nonEmptyArray = (message: string) =>
  z
    .string()
    .array()
    .refine((arr) => arr.length > 0, { error: message });

export const validateRequiredNumber = (fieldName: string) =>
  z.number({
    error: `${fieldName} is required.`,
  });

export const nullableNumberInput = () =>
  z.preprocess((v) => (v === '' || v === undefined ? null : v), z.number().nullable());

export const nullableCoercedNumberInput = () =>
  z.preprocess((v) => (v === '' || v === undefined ? null : v), z.coerce.number().nullable());

const validatePassword = () => z.string().min(8, 'Password must be at least 8 characters');

export const addCreatePasswordValidation = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) => {
  return schema
    .extend({
      password: validatePassword(),
      confirmPassword: z.string(),
    })
    .refine(
      (data) => {
        const { password, confirmPassword } = data as { password: string; confirmPassword: string };
        return password === confirmPassword;
      },
      {
        error: 'Passwords do not match',
        path: ['confirmPassword'],
      }
    );
};

export const addResetPasswordValidation = <T extends z.ZodRawShape>(schema: z.ZodObject<T>) => {
  return schema
    .extend({
      password: z.string().optional(),
      confirmPassword: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const { password, confirmPassword } = data as { password?: string; confirmPassword?: string };

      if (password !== confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Passwords do not match',
          path: ['confirmPassword'],
        });
      }

      if (password?.trim()) {
        const result = validatePassword().safeParse(password);
        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['password'],
            });
          });
        }
      }
    })
    .transform((data) => {
      const { password, confirmPassword, ...rest } = data as typeof data & {
        password?: string;
        confirmPassword?: string;
      };
      return password ? { password, ...rest } : rest;
    });
};
